// BL-1528 — registry entries for the unified staff-internal notes log (ADR 027).
// BL-1833 PR 6b — every verb declares its grant through the ADR 062 — Access
// Grants gate: a note's permission is its ENTITY's, picked per entity type.
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute } from './define-admin-route';
import type { PolymorphicEntityGrant } from './define-admin-route';
import { LOT_EDIT_MODAL_READ } from './lots';
// Type-only import (erased at build) — keeps the registry client-bundle-safe.
import type { InternalNoteDto, MentionableStaffDto } from '../../schemas/responses/internal-note';

/**
 * Who may READ a record's notes, per entity type (ADR 052 — Internal Notes § 5).
 *
 * - `customer`: either customer-read grant — Accounting reaches the customer
 *   page on `customer.read_limited` only (BL-1530).
 * - `lot`: every role that can open the lot at all (`LOT_EDIT_MODAL_READ`) —
 *   Admin, a Cataloger from /inventory, Accounting. ADR 052 § 5 left lot notes
 *   open to any member; a polymorphic entity may not be `{ none }`, so this is
 *   the grant that admits every real role (ADR 062 — Access Grants § 3,
 *   BL-1833 PR 6b amendment).
 * - `auction`: `auction.read` — Admin and Accounting (BL-1668, BL-2283).
 * - `invoice`: `invoice.read` — the invoices.view permission (BL-2026, MN11).
 * - `settlement`: `settlement.read` — the settlements.view permission (BL-2525).
 *
 * Also the EDIT grant: editing is author-only (ADR 052 § 4), decided by the
 * service, so PATCH only needs the caller to see the record.
 */
export const NOTE_READ = {
  customer: { anyOf: ['customer.read', 'customer.read_limited'] },
  lot: LOT_EDIT_MODAL_READ,
  auction: 'auction.read',
  invoice: 'invoice.read',
  settlement: 'settlement.read',
} as const satisfies Record<string, PolymorphicEntityGrant>;

/**
 * Who may ADD or DELETE a note, per entity type. As {@link NOTE_READ}, except an
 * invoice note needs `invoice.update` (invoices.manage), so read-only invoice
 * viewers can't write one (BL-2026). A settlement note likewise needs
 * `settlement.update` (settlements.manage, BL-2525). A customer note follows the customer's
 * read grants: Accounting, on `customer.read_limited`, adds and deletes customer
 * notes like an Admin (Dan Saper, 2026-10-08, BL-2464, reversing BL-1530 RAC 4).
 * An auction note gates on `auction.read` for write too (Dan Saper, 2026-09-10,
 * BL-1668).
 */
const NOTE_WRITE = {
  customer: NOTE_READ.customer,
  lot: LOT_EDIT_MODAL_READ,
  auction: 'auction.read',
  invoice: 'invoice.update',
  settlement: 'settlement.update',
} as const satisfies Record<string, PolymorphicEntityGrant>;

/** The five record types a note may hang off, each resolving to its own row. */
const noteEntities = <G extends Record<'customer' | 'lot' | 'auction' | 'invoice' | 'settlement', PolymorphicEntityGrant>>(
  grants: G,
) =>
  ({
    customer: { table: 'customer', grant: grants.customer },
    lot: { table: 'lot', grant: grants.lot },
    auction: { table: 'auction', grant: grants.auction },
    invoice: { table: 'invoice', grant: grants.invoice },
    settlement: { table: 'settlement', grant: grants.settlement },
  }) as const;

export const internal_notes = {
  // BL-2466 — the @ list for a note on a saved record: the record's house, its
  // members marked by whether they can open this kind of record (D5). The same
  // polymorphic declaration as the list read, so only a caller who can read the
  // record's notes gets it, and the tenant is the record's own (ADR 052).
  mentionables: defineAdminRoute({
    path: '/api/internal-notes/mentionables',
    params: {},
    methods: {
      GET: {
        grant: { none: "per entity — the entity's note-read grant, see tenant.entities" },
        tenant: {
          from: 'polymorphic',
          in: 'query',
          typeField: 'entityType',
          idField: 'entityId',
          entities: noteEntities(NOTE_READ),
        },
        response: responseType<MentionableStaffDto[]>(),
        query: { entityType: '', entityId: '' },
      },
    },
  }),
  // BL-2466 — the @ list for notes typed on an Add-lot form, before the lot
  // exists: the form names its house. The grant is the lot's note-read grant.
  lot_create_mentionables: defineAdminRoute({
    path: '/api/tenants/:id/lot-note-mentionables',
    params: { id: '' },
    methods: {
      GET: {
        grant: NOTE_READ.lot,
        tenant: { from: 'record', table: 'tenant', param: 'id' },
        response: responseType<MentionableStaffDto[]>(),
      },
    },
  }),
  index: defineAdminRoute({
    path: '/api/internal-notes',
    params: {},
    methods: {
      // The list is scoped by entity, newest first. The tenant is the entity's,
      // never the request's (ADR 052). `includeDeleted` is a super-admin-only
      // widening for support lookups; a normal operator sending it gets the
      // same live-only list.
      GET: {
        grant: { none: "per entity — the entity's note-read grant, see tenant.entities" },
        tenant: {
          from: 'polymorphic',
          in: 'query',
          typeField: 'entityType',
          idField: 'entityId',
          entities: noteEntities(NOTE_READ),
        },
        response: responseType<InternalNoteDto[]>(),
        query: { entityType: '', entityId: '', includeDeleted: '' },
      },
      POST: {
        grant: { none: "per entity — the entity's note-write grant, see tenant.entities" },
        tenant: {
          from: 'polymorphic',
          typeField: 'entityType',
          idField: 'entityId',
          entities: noteEntities(NOTE_WRITE),
        },
        response: responseType<InternalNoteDto>(),
      },
    },
  }),
  detail: defineAdminRoute({
    path: '/api/internal-notes/:noteId',
    params: { noteId: '' },
    // The gate loads the NOTE (its tenant is the note's own) and picks the
    // grant from its `entity_type`; a type the map does not list is a 404.
    // PATCH returns the updated note so the row can re-render from the server's
    // copy (including the flipped `isEdited`) rather than guessing locally.
    // DELETE soft-deletes and returns no data payload, so it carries no
    // response marker.
    methods: {
      PATCH: {
        grant: { none: "per entity type — the entity's note-READ grant, see tenant.grantBy; author-only is the service's rule (ADR 052 § 4)" },
        tenant: {
          from: 'record',
          table: 'internal_note',
          param: 'noteId',
          grantBy: { column: 'entityType', grants: NOTE_READ },
        },
        response: responseType<InternalNoteDto>(),
      },
      // `withDeleted` (BUG-2060): an already-deleted note still passes the tenant and grant checks,
      // so the handler can name it as already deleted (a repeat delete) apart from the gate's 404
      // for a foreign or unknown note. The handler never restamps it.
      DELETE: {
        grant: { none: "per entity type — the entity's note-write grant, see tenant.grantBy" },
        tenant: {
          from: 'record',
          table: 'internal_note',
          param: 'noteId',
          withDeleted: true,
          grantBy: { column: 'entityType', grants: NOTE_WRITE },
        },
      },
    },
  }),
};
